import { useI18n } from "../lib/i18n";
import { useState, useRef, useEffect, useMemo, type ReactNode } from "react";
import { type ContractStatusFilter, type Contract, type Milestone, type ListStatusFilter, type DateSort, type PriceSort, type ContractTab, errorMessage, contractStatusKey, contractStatusLabel, statusChipClass, STATUS_FILTERS, peso, agreementWorkspaceContract } from "./contracts/model";
import { useAuth } from "../lib/auth";
import { useLocation, useSearch } from "wouter";
import { type Order, api } from "../lib/api";
import type { EngagementSummary } from "../lib/api-client/contracts";
import { purchaseStatusLabel, purchaseStatusClass, PURCHASE_STATUS_FILTERS } from "../lib/purchaseStatus";
import { PurchaseWorkspace } from "../components/PurchaseWorkspace";
import { ContractWorkspace } from "./contracts/ContractWorkspace";
import { ReleaseConfirmation } from "../components/ReleaseConfirmation";
import { ProjectAgreementLookup } from "../components/ProjectAgreement";
import { MobileContractsList } from "../components/MobileContractsList";
import { ContractDesktopList } from "./contracts/ContractDesktopList";
import { EngagementWorkspace } from "./contracts/EngagementWorkspace";
import { profileKey } from "../lib/profileSession";
import { engagementListRows, mobileEngagementRows } from "./contracts/engagementList";
import { PullToRefresh, RefreshError } from "../components/PullToRefresh";
import { isNativeApp } from "../lib/platform";
import { ignoreOverviewAbort, overviewAccessLost, overviewFailureKey, overviewProfileLost, useOverviewRead } from "../lib/useOverviewRead";

import { refreshFamily, settleRefreshFamilies } from "../lib/useRefreshOwner";

export default function Contracts() {
  const { t } = useI18n();
  const [mobileView, setMobileView] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  const [mobileStage, setMobileStage] = useState<Exclude<ContractStatusFilter, "all">>("offer");
  const [mobileSearch, setMobileSearch] = useState("");
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const urlSearch = useSearch();
  const reads = useOverviewRead(`${profileKey(user)}:contracts`);
  const overviewView = useRef("");
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractsLoaded, setContractsLoaded] = useState(false);
  const [engagements, setEngagements] = useState<EngagementSummary[]>([]);
  const [engagementsLoaded, setEngagementsLoaded] = useState(false);
  const [engagementError, setEngagementError] = useState<string | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [purchasesLoaded, setPurchasesLoaded] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [contractReadError, setContractReadError] = useState<string | null>(null);
  const [releaseSelection, setReleaseSelection] = useState<{ contractId: string; viewerId: number; milestone: Milestone } | null>(null);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releaseRefreshFailed, setReleaseRefreshFailed] = useState(false);
  const releasePending = useRef(false);
  const [statusFilter, setStatusFilter] = useState<ListStatusFilter>("all");
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState<ListStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [dateSort, setDateSort] = useState<DateSort>("newest");
  const [priceSort, setPriceSort] = useState<PriceSort>(null);
  const [listOverlayOpen, setListOverlayOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ContractTab>(() => new URLSearchParams(urlSearch).get("tab") === "activity" ? "activity" : "overview");
  useEffect(() => { setReleaseSelection(null); setReleaseError(null); }, [user?.id, urlSearch]);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setMobileView(media.matches);
    media.addEventListener("change", update);
    update();
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  const viewerId = useRef(user?.id);
  viewerId.current = user?.id;
  async function readContracts(signal: AbortSignal, assertCurrent: () => void, manual = false) {
    const requestedView = overviewView.current;
    // Desktop type counts and mobile agreement mirrors/Job rows require all
    // three families. A contracts-only background poll is not a manual refresh.
    const { profileLost, failureKey, familyErrors } = await settleRefreshFamilies([
      refreshFamily(() => api.listContracts(signal).then(value => { if (!Array.isArray(value?.contracts)) throw new Error("Missing contracts."); return value; }),
        value => { setContracts(value.contracts); setErr(null); }, () => setContracts([])),
      refreshFamily(() => api.myOrders(signal).then(value => { if (!Array.isArray(value?.purchases) || !Array.isArray(value?.sales)) throw new Error("Missing purchases."); return value; }),
        value => setOrders([...value.purchases, ...value.sales]), () => setOrders([])),
      refreshFamily(() => api.engagements(signal), setEngagements, () => setEngagements([])),
    ], () => {
      assertCurrent();
      if (manual && requestedView !== overviewView.current) throw new DOMException("Overview view changed.", "AbortError");
    });
    setReleaseRefreshFailed(profileLost || !!familyErrors[0]);
    [setContractReadError, setPurchaseError, setEngagementError].forEach((setError, index) => {
      const key = profileLost ? failureKey : familyErrors[index];
      setError(key ? t(key === "refresh.overviewPartial" ? "refresh.overviewLoadError" : key) : null);
    });
    setContractsLoaded(true); setPurchasesLoaded(true); setEngagementsLoaded(true);
    if (manual && failureKey) throw new RefreshError(t(failureKey));
  }

  async function load() { await reads.run((signal, assertCurrent) => readContracts(signal, assertCurrent)).catch(ignoreOverviewAbort); }
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);
  useEffect(() => {
    if (!user) return;
    let active = true;
    const refresh = () => {
      if (document.visibilityState !== "visible" || busy) return;
      void reads.run(async (signal, assertCurrent) => {
        const [result] = await Promise.allSettled([api.listContracts(signal)]);
        assertCurrent();
        if (!active) return;
        if (result.status === "fulfilled" && Array.isArray(result.value?.contracts)) {
          setContracts(result.value.contracts);
          // Fresh contract data restores release eligibility; polling must not
          // dismiss unrelated command errors or other dataset failures.
          setReleaseRefreshFailed(false); setContractReadError(null);
        }
        else if (result.status === "rejected" && overviewAccessLost(result.reason)) {
          setContracts([]); setReleaseRefreshFailed(true); setContractReadError(t(overviewFailureKey([result.reason])));
          if (overviewProfileLost(result.reason)) { setOrders([]); setEngagements([]); }
        }
        // A transient polling failure retains the last readable projection.
      }, { background: true }).catch(ignoreOverviewAbort);
    };
    const timer = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [user?.id, busy]);
  async function fund(id: string) {
    reads.cancel();
    setBusy(id); setErr(null);
    try { await api.fundContract(id); await load(); } catch (error: unknown) { setErr(errorMessage(error, "Could not fund.")); } finally { setBusy(null); }
  }
  async function release() {
    // [items.purchase-stages](../../../docs/product/items.md#items-purchase-stages): confirm only the current buyer's pending stage and unchanged displayed terms.
    if (releasePending.current || busy || !canRelease || !releaseSelection) return;
    const selection = releaseSelection;
    reads.cancel();
    releasePending.current = true;
    setBusy(selection.milestone.id); setReleaseError(null);
    try {
      await api.releaseMilestone(selection.contractId, selection.milestone.id);
      if (selection.viewerId !== viewerId.current) return;
      setReleaseSelection(null);
      await load();
    } catch (error: unknown) {
      if (selection.viewerId !== viewerId.current) return;
      await load();
      if (selection.viewerId !== viewerId.current) return;
      setReleaseError(errorMessage(error, "Could not release."));
    } finally { releasePending.current = false; setBusy(null); }
  }
  async function acceptOffer(id: string, signatureName?: string) {
    reads.cancel();
    setBusy(id + ":offer"); setErr(null);
    try { await api.acceptOffer(id, signatureName, contracts.find(contract => contract.id === id)?.agreementDocument?.id); await load(); } catch (error: unknown) { setErr(errorMessage(error, "Could not accept.")); } finally { setBusy(null); }
  }
  async function declineOffer(id: string, expectedProposalId?: string) {
    reads.cancel();
    setBusy(id + ":offer"); setErr(null);
    try { await api.declineOffer(id, expectedProposalId); await load(); } catch (error: unknown) { setErr(errorMessage(error, "Could not decline.")); } finally { setBusy(null); }
  }
  async function withdrawOffer(id: string) {
    reads.cancel();
    setBusy(id + ":offer"); setErr(null);
    try { await api.withdrawOffer(id); await load(); } catch (error: unknown) { setErr(errorMessage(error, "Could not withdraw.")); } finally { setBusy(null); }
  }

  const listType = new URLSearchParams(urlSearch).get("type") === "purchase" ? "purchase" : "work";
  const workContracts = useMemo(() => contracts.filter(({ sourceType }) => sourceType !== "order"), [contracts]);
  const unlinkedEngagements = useMemo(() => engagements.filter(row => !workContracts.some(contract => contract.sourceType === "job" && contract.sourceId === row.id)), [engagements, workContracts]);
  // [items.purchase-workspace](../../../docs/product/items.md#items-purchase-workspace): actual orders own purchase status; mirrors only supply the agreement document.
  const listRows = useMemo(() => listType === "purchase" ? orders.map((order) => ({
    id: order.id, title: order.itemTitle, amount: order.total, createdAt: order.createdAt,
    role: order.role, counterparty: order.role === "buyer" ? order.sellerName : order.buyerName,
    sourceLabel: order.mode === "rent" ? "Historical rental" : "Purchase",
    statusKey: order.status, statusLabel: purchaseStatusLabel(order.status, t), statusClass: purchaseStatusClass(order.status),
    href: `/contracts?order=${encodeURIComponent(order.id)}`,
  })) : [...workContracts.map((contract) => ({
    id: contract.id, title: contract.title, amount: contract.amount, createdAt: contract.createdAt,
    role: contract.sourceType === "job" ? contract.role === "buyer" ? "employer" : "worker" : contract.role, counterparty: contract.counterparty, sourceLabel: contract.sourceType === "rfq_items" ? "Items RFQ" : contract.sourceType.toUpperCase(),
    statusKey: contractStatusKey(contract), statusLabel: contractStatusLabel(contract, "list", t), statusClass: statusChipClass(contractStatusKey(contract)),
    href: `/contracts?contract=${encodeURIComponent(contract.id)}`,
  })), ...engagementListRows(unlinkedEngagements)], [listType, orders, workContracts, unlinkedEngagements, t]);
  const activeFilter = listType === "purchase" ? purchaseStatusFilter : statusFilter;
  const statusFilters = listType === "purchase" ? PURCHASE_STATUS_FILTERS : STATUS_FILTERS;
  const listLoaded = listType === "purchase" ? purchasesLoaded : contractsLoaded && engagementsLoaded;
  const listError = listType === "purchase" ? purchaseError : err || contractReadError || engagementError;
  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return listRows
      .filter((row) => activeFilter === "all" || row.statusKey === activeFilter)
      .filter((row) => !query || `${row.title} ${row.counterparty ?? ""}`.toLowerCase().includes(query))
      .sort((left, right) => {
        const leftTime = new Date(left.createdAt).getTime();
        const rightTime = new Date(right.createdAt).getTime();
        const dateResult = dateSort === "newest" ? rightTime - leftTime : dateSort === "oldest" ? leftTime - rightTime : 0;
        if (dateResult !== 0) return dateResult;
        if (right.amount == null || left.amount == null) return 0;
        return priceSort === "highest" ? right.amount - left.amount : priceSort === "lowest" ? left.amount - right.amount : 0;
      });
  }, [listRows, dateSort, priceSort, search, activeFilter]);

  const selectedId = new URLSearchParams(urlSearch).get("contract");
  const selectedOrderId = new URLSearchParams(urlSearch).get("order");
  const selectedEngagementId = new URLSearchParams(urlSearch).get("engagement");
  const selectedContract = contracts.find((contract) => selectedId ? contract.id === selectedId : selectedEngagementId ? contract.sourceType === "job" && contract.sourceId === selectedEngagementId : selectedOrderId && contract.sourceType === "order" && contract.sourceId === selectedOrderId) ?? null;
  useEffect(() => { if (selectedEngagementId) setActiveTab(new URLSearchParams(urlSearch).get("tab") === "agreement" ? "agreement" : "work"); }, [selectedEngagementId, urlSearch]);
  const releaseMilestone = selectedContract?.milestones.find(stage => stage.id === releaseSelection?.milestone.id);
  const canRelease = contractsLoaded && !releaseRefreshFailed && releaseSelection?.viewerId === user?.id &&
    releaseSelection?.contractId === selectedContract?.id && selectedContract?.sourceType !== "order" &&
    selectedContract?.role === "buyer" && selectedContract.status === "funded" &&
    releaseMilestone?.status === "pending" && releaseMilestone.amount === releaseSelection?.milestone.amount &&
    releaseMilestone.label === releaseSelection?.milestone.label;
  const statusCount = (status: string) => status === "all" ? listRows.length : listRows.filter((row) => row.statusKey === status).length;
  const openAgreement = (href: string) => { navigate(href); setActiveTab("overview"); window.scrollTo(0, 0); };
  const download = async (contract: Contract, kind: "contract" | "receipt") => {
    setErr(null);
    try { await api.downloadContractPdf(contract.id, kind); } catch (error: unknown) { setErr(errorMessage(error, "Download failed.")); }
  };

  const refreshIdentity = JSON.stringify([profileKey(user), urlSearch, mobileView, mobileStage, mobileSearch,
    statusFilter, purchaseStatusFilter, search, dateSort, priceSort, busy, listOverlayOpen]);
  overviewView.current = refreshIdentity;
  const withRefresh = (content: ReactNode) => selectedId || selectedOrderId || selectedEngagementId ? content : (
    <PullToRefresh identity={refreshIdentity} disabled={Boolean(busy || releaseSelection || listOverlayOpen) || !contractsLoaded || !purchasesLoaded || !engagementsLoaded}
      onRefresh={signal => reads.run((readSignal, assertCurrent) => readContracts(readSignal, assertCurrent, true), { signal })}>
      {content}
    </PullToRefresh>
  );

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  if (selectedEngagementId && !selectedContract) return <>
    {(err || contractReadError) && <div role="alert" className="container-x py-3 text-sm text-brand-red">{err || contractReadError} <button type="button" className="underline" onClick={() => void load()}>Retry payment details</button></div>}
    <EngagementWorkspace key={`${profileKey(user)}:engagement:${selectedEngagementId}`}
    id={selectedEngagementId} isMobile={mobileView} onReload={() => void load()} initialTab={new URLSearchParams(urlSearch).get("tab") === "agreement" ? "agreement" : "work"} onBack={(status) => {
      setMobileStage(status || "active"); setStatusFilter(status || "all"); setSearch(""); setMobileSearch("");
      navigate("/contracts"); void load();
    }} /></>;

  const purchaseId = selectedContract?.sourceType === "order" ? selectedContract.sourceId : !selectedId ? selectedOrderId : null;
  if (purchaseId) return <PurchaseWorkspace key={`${user.id}:${purchaseId}`} orderId={purchaseId}
    agreement={selectedContract?.sourceType === "order" ? selectedContract : null} agreementLoading={!contractsLoaded}
    onBack={() => navigate("/contracts?type=purchase")} onReload={load} />;

  const renderWorkspace = (selectedContract: Contract) => (
      <> <ContractWorkspace
        contract={selectedContract}
        isMobile={mobileView}
        activeTab={activeTab}
        busy={busy}
        error={err || contractReadError}
        onBack={() => { navigate("/contracts"); if (selectedContract.sourceType === "job") void load(); }}
        onTabChange={setActiveTab}
        onFund={() => fund(selectedContract.id)}
        onRelease={(milestoneId) => {
          const milestone = selectedContract.milestones.find(stage => stage.id === milestoneId);
          if (busy || releasePending.current || !contractsLoaded || releaseRefreshFailed || selectedContract.role !== "buyer" || selectedContract.status !== "funded" || milestone?.status !== "pending") return;
          setReleaseError(null);
          setReleaseSelection({ contractId: selectedContract.id, viewerId: user.id, milestone: { ...milestone } });
        }}
        onAccept={(signatureName) => acceptOffer(selectedContract.id, signatureName)}
        onDecline={() => declineOffer(selectedContract.id, selectedContract.agreement?.id)}
        onWithdraw={() => withdrawOffer(selectedContract.id)}
        onDownload={(kind) => download(selectedContract, kind)}
        onReload={load}
        onNegotiate={() => selectedContract.threadId ? navigate(selectedContract.negotiationId ? `/messages/${encodeURIComponent(selectedContract.threadId)}?agreement=${encodeURIComponent(selectedContract.negotiationId)}` : `/messages/${encodeURIComponent(selectedContract.threadId)}?negotiate=${encodeURIComponent(selectedContract.id)}`) : setErr("The contract conversation is unavailable.")}
      />
      {releaseSelection && releaseSelection.contractId === selectedContract.id && releaseSelection.viewerId === user.id && <ReleaseConfirmation
        description={selectedContract.sourceType === "rfq_items"
          ? `Confirm you received all items in this agreement. This releases ${peso(releaseSelection.milestone.amount)} from escrow and completes the agreement.`
          : selectedContract.sourceType === "job"
            ? `Release ${peso(releaseSelection.milestone.amount)} for "${releaseSelection.milestone.label}" to the worker? This is a manual release. Configured payout fees may be deducted.`
            : `Is "${releaseSelection.milestone.label}" complete? Release ${peso(releaseSelection.milestone.amount)} to the ${selectedContract.sourceType === "business" ? "seller" : "provider"}.`}
        busy={busy !== null} disabled={!canRelease} error={releaseError || (releaseRefreshFailed ? contractReadError || err : null) || (!busy && !canRelease ? "This milestone has changed or is no longer available. Close and review the contract." : null)}
        onClose={() => { if (!releasePending.current && !busy) setReleaseSelection(null); }} onConfirm={release}
      />}
      </>
    );
  if (selectedContract) return renderWorkspace(selectedContract);
  if (selectedId?.startsWith("agreement-")) return <ProjectAgreementLookup key={`${user.id}:${selectedId}`} negotiationId={selectedId} onResolved={agreement => renderWorkspace(agreementWorkspaceContract(agreement))} />;

  // [contracts.workspace-navigation](../../../docs/product/contracts.md#contracts-workspace-navigation): the mobile projection uses the same party-owned contracts and detail route.
  // Keep its stage/search state here so returning from a contract preserves the list.
  if (mobileView) {
    return withRefresh(
      <div className="container-x mobile-contracts-page py-6 pb-24 max-w-5xl">
        {listError && <div className="mb-3 text-sm text-brand-red" role="alert">{listError} {!isNativeApp() && <button type="button" className="underline" onClick={() => void load()}>Retry</button>}</div>}
        {!listLoaded && <p role="status" className="mb-3 text-sm text-mut">Loading work…</p>}
        {listLoaded && (contracts.length > 0 || engagements.length > 0 || !listError) && <>
        <MobileContractsList
          rows={[...contracts.map((contract) => {
            const stage = contractStatusKey(contract);
            return {
              id: contract.id, title: contract.title, counterparty: contract.counterparty || "", href: `/contracts?contract=${encodeURIComponent(contract.id)}`,
              roleLabel: contract.sourceType === "job" ? contract.role === "buyer" ? "Employer" : "Worker" : t(`contracts.mobile.${contract.role}`), stage,
              statusLabel: contract.sourceType === "order" && contract.status === "funded"
                ? t("contracts.mobile.paid") : t(`contracts.mobile.${stage}`),
              statusClass: statusChipClass(stage), amount: contract.amount, createdAt: contract.createdAt,
            };
          }), ...mobileEngagementRows(unlinkedEngagements)]}
          stages={STATUS_FILTERS.filter(({ key }) => key !== "all").map(({ key }) => ({ key, label: t(`contracts.mobile.${key}`) }))}
          selectedStage={mobileStage} query={mobileSearch} onQueryChange={setMobileSearch}
          onStageChange={(key) => {
            const stage = STATUS_FILTERS.find((entry) => entry.key === key);
            if (stage && stage.key !== "all") setMobileStage(stage.key);
          }}
          onOpen={openAgreement}
        />
        </>}
      </div>
    );
  }

  return withRefresh(<ContractDesktopList listType={listType} navigate={navigate} contractsLoaded={contractsLoaded && engagementsLoaded} workCount={workContracts.length + unlinkedEngagements.length}
    purchasesLoaded={purchasesLoaded} orders={orders} statusFilters={statusFilters} activeFilter={activeFilter}
    setPurchaseStatusFilter={setPurchaseStatusFilter} setStatusFilter={setStatusFilter} t={t} listLoaded={listLoaded}
    statusCount={statusCount} search={search} setSearch={setSearch} dateSort={dateSort} setDateSort={setDateSort}
    priceSort={priceSort} setPriceSort={setPriceSort} listError={listError} load={load} visibleRows={visibleRows}
    listRows={listRows} openAgreement={openAgreement} onOverlayChange={setListOverlayOpen} />);
}
